import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode, RefObject } from "react";

type AnchoredPopoverProps = {
  anchorRef: RefObject<HTMLElement>;
  width: number;
  onClose: () => void;
  children: ReactNode;
  className?: string;
};

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 4;

const AnchoredPopover = ({ anchorRef, width, onClose, children, className = "" }: AnchoredPopoverProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const openUpwards = panelHeight > 0 && spaceBelow < panelHeight + VIEWPORT_MARGIN && anchorRect.top > panelHeight;

    const top = openUpwards ? anchorRect.top - panelHeight - ANCHOR_GAP : anchorRect.bottom + ANCHOR_GAP;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchorRect.right - width, maxLeft));

    setPosition((previous) => (previous && previous.top === top && previous.left === left ? previous : { top, left }));
  }, [anchorRef, width]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleDismiss = () => onClose();

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("resize", handleDismiss);
    window.addEventListener("scroll", handleDismiss, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("resize", handleDismiss);
      window.removeEventListener("scroll", handleDismiss, true);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div ref={panelRef} style={{ position: "fixed", top: position?.top ?? -9999, left: position?.left ?? -9999, width, visibility: position ? "visible" : "hidden" }} className={`tw-z-[1000] tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-shadow-autoSuggest ${className}`}>
      {children}
    </div>,
    document.body
  );
};

export default AnchoredPopover;
